import {
  ServiceRequest,
  DocumentReference,
  DiagnosticReport,
  Patient,
  Observation,
  Bundle,
  Task,
  Specimen,
} from '@medplum/fhirtypes';
import { OrderFormData } from '../components/OrderForm';
import { ANSWER_DELIVERY_OPTIONS } from '../constants/deliveryOptions';
import { ARKON, EXTERNAL_CS, HL7, SYSTEMS } from '@consts/systems.consts';
import { OrderSubState } from '../lims-types';
import {
  CONSENT_DOCUMENT_CODING,
  CONSENT_STATUS_TAG_CODES,
} from '@/consts/consent.consts';
import { TEST_RESULT_CODES } from '@consts/genetic-tests';
import {
  MedplumClient,
  formatHumanName,
  getReferenceString,
} from '@medplum/core';
import { hasAuthorizedPerson } from './common-helpers';

export function createServiceRequestFromForm(
  formData: OrderFormData,
  orderId: string,
  status:
    | 'draft'
    | 'active'
    | 'on-hold'
    | 'revoked'
    | 'completed'
    | 'entered-in-error'
    | 'unknown' = 'draft',
  encounterId: string | undefined = undefined
): ServiceRequest {
  return {
    resourceType: 'ServiceRequest',
    meta: {
      tag: [
        {
          system: ARKON.SD.LAB_ORDER_SUBSTATE,
          code: OrderSubState.OrderCreated,
        },
        {
          system: ARKON.SD.CONSENT_STATUS_TAG,
          code: CONSENT_STATUS_TAG_CODES.NOT_ANSWERED.code,
        },
      ],
    },
    status,
    intent: 'order',
    identifier: [
      {
        use: 'usual',
        system: ARKON.CS.SERVICE_REQUEST.IDS.BASE,
        value: orderId,
      },
    ],
    category: [
      {
        coding: [
          {
            system: SYSTEMS.SNOMED,
            code: EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE,
            display: 'Laboratory procedure',
          },
        ],
      },
    ],
    code: {
      coding: [
        {
          system: ARKON.CS.GENETIC_TESTS.BASE,
          code: formData.testCode,
          display: formData.testDisplay,
        },
      ],
    },
    subject: { reference: `Patient/${formData.patient?.id}` },
    requester: { reference: `Practitioner/${formData.requester?.id}` },
    authoredOn: formData.orderDate
      ? new Date(formData.orderDate).toISOString()
      : new Date().toISOString(),
    ...(encounterId && {
      encounter: { reference: `Encounter/${encounterId}` },
    }),
    note: formData.clinicalNotes
      ? [
          {
            text: formData.clinicalNotes,
            time: new Date().toISOString(),
          },
        ]
      : undefined,
    reasonCode: formData.referralReason
      ? [
          {
            coding: [
              {
                system: ARKON.CS.SERVICE_REQUEST.REFERRAL_REASON.BASE,
                code: formData.referralReason,
                display: formData.referralReason,
              },
            ],
            text: formData.referralReason,
          },
        ]
      : undefined,
    extension: buildExtensions(formData),
  };
}

function buildExtensions(formData: OrderFormData): any[] {
  const answerDelivery = Array.isArray(formData.answerDelivery)
    ? formData.answerDelivery
    : formData.answerDelivery
      ? [formData.answerDelivery]
      : [];

  const deliveryExtension: any = {
    url: ARKON.SD.DELIVERY_PREFERENCE,
    extension: [
      ...answerDelivery.map((method) => ({
        url: 'method',
        valueCodeableConcept: {
          coding: [
            {
              system: ARKON.CS.DELIVERY.METHODS.BASE,
              code: method,
              display:
                ANSWER_DELIVERY_OPTIONS.find((o) => o.value === method)
                  ?.label || method,
            },
          ],
        },
      })),
      ...(hasAuthorizedPerson(answerDelivery) && formData.authorizedPerson
        ? [
            {
              url: 'authorizedPerson',
              valueHumanName: {
                text: formData.authorizedPerson,
              },
            },
          ]
        : []),
    ],
  };

  const extensions: any[] = [
    {
      url: ARKON.SD.SPECIMEN_TYPE,
      valueCodeableConcept: formData.sampleType,
    },
    deliveryExtension,
  ];

  if (formData.symptomOnsetAge || formData.firstSymptom) {
    extensions.push({
      url: ARKON.SD.CLINICAL_INFORMATION,
      extension: [
        ...(formData.symptomOnsetAge
          ? [
              {
                url: 'symptomOnsetAge',
                valueString: formData.symptomOnsetAge,
              },
            ]
          : []),
        ...(formData.firstSymptom
          ? [
              {
                url: 'firstSymptom',
                valueString: formData.firstSymptom,
              },
            ]
          : []),
      ],
    });
  }

  return extensions;
}

export function extractFormDataFromServiceRequest(
  serviceRequest: ServiceRequest
): Partial<OrderFormData> {
  const formData: Partial<OrderFormData> = {};

  formData.testCode = serviceRequest.code?.coding?.[0]?.code || '';
  formData.testDisplay = serviceRequest.code?.coding?.[0]?.display || '';
  formData.referralReason =
    serviceRequest.reasonCode?.[0]?.text ||
    serviceRequest.reasonCode?.[0]?.coding?.[0]?.display ||
    '';
  formData.clinicalNotes = serviceRequest.note?.[0]?.text || '';
  formData.orderDate = serviceRequest.authoredOn
    ? new Date(serviceRequest.authoredOn)
    : new Date();

  // Get sample type from extension (supports both old and new structure)
  const sampleTypeExt = serviceRequest.extension?.find(
    (ext) =>
      ext.url === ARKON.CS.SPECIMEN.TYPE.BASE ||
      ext.url === ARKON.SD.SPECIMEN_TYPE
  );
  formData.sampleType = sampleTypeExt?.valueCodeableConcept;

  // Get delivery preferences from extension (supports both old and new structure)
  const deliveryPrefExt = serviceRequest.extension?.find(
    (ext) => ext.url === ARKON.SD.DELIVERY_PREFERENCE
  );
  if (deliveryPrefExt?.extension) {
    const methodExts = deliveryPrefExt.extension.filter(
      (e) => e.url === 'method'
    );
    const methods = methodExts
      .map((e) => e.valueCodeableConcept?.coding?.[0]?.code)
      .filter((c): c is string => !!c);
    formData.answerDelivery =
      methods.length > 0 ? methods : ([] as unknown as string[]);

    const personExt = deliveryPrefExt.extension.find(
      (e) => e.url === 'authorizedPerson'
    );
    if (personExt?.valueHumanName?.text) {
      formData.authorizedPerson = personExt.valueHumanName.text;
    }
  } else {
    // Old structure fallback (single value)
    const answerDeliveryExt = serviceRequest.extension?.find(
      (ext) => ext.url === ARKON.CS.DELIVERY.ANSWER.BASE
    );
    const singleMethod = answerDeliveryExt?.valueString || '';
    formData.answerDelivery = singleMethod ? [singleMethod] : [];

    const authorizedPersonExt = serviceRequest.extension?.find(
      (ext) => ext.url === ARKON.CS.SERVICE_REQUEST.AUTHORIZED_PERSON.BASE
    );
    formData.authorizedPerson = authorizedPersonExt?.valueString || '';
  }

  // Get clinical information from extension
  const clinicalInfoExt = serviceRequest.extension?.find(
    (ext) => ext.url === ARKON.SD.CLINICAL_INFORMATION
  );
  if (clinicalInfoExt?.extension) {
    const ageExt = clinicalInfoExt.extension.find(
      (e) => e.url === 'symptomOnsetAge'
    );
    formData.symptomOnsetAge = ageExt?.valueString || '';

    const symptomExt = clinicalInfoExt.extension.find(
      (e) => e.url === 'firstSymptom'
    );
    formData.firstSymptom = symptomExt?.valueString || '';
  } else {
    // Old structure fallback
    const symptomOnsetAgeExt = serviceRequest.extension?.find(
      (ext) => ext.url === ARKON.CS.CONDITIONS.SYMPTOM_ONSET_AGE.BASE
    );
    formData.symptomOnsetAge = symptomOnsetAgeExt?.valueString || '';

    const firstSymptomExt = serviceRequest.extension?.find(
      (ext) => ext.url === ARKON.CS.CONDITIONS.FIRST_SYMPTOM.BASE
    );
    formData.firstSymptom = firstSymptomExt?.valueString || '';
  }

  return formData;
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      resolve(base64.split(',')[1]);
    };
    reader.onerror = (error) => reject(error);
  });
}

export function createConsentDocument(
  file: File,
  base64Data: string,
  patientId: string
): DocumentReference {
  return {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [CONSENT_DOCUMENT_CODING],
    },
    subject: { reference: `Patient/${patientId}` },
    date: new Date().toISOString(),
    content: [
      {
        attachment: {
          contentType: file.type,
          data: base64Data,
          title: file.name,
        },
      },
    ],
  };
}

export function createOrderDocument(
  file: File,
  binaryReferenceUrl: string,
  patientId: string,
  orderId: string
): DocumentReference {
  return {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [
        {
          system: SYSTEMS.LOINC,
          code: EXTERNAL_CS.LOINC.CODES.ORDER_DOCUMENT,
          display: 'Laboratory order / requisition',
        },
      ],
    },
    subject: { reference: `Patient/${patientId}` },
    date: new Date().toISOString(),
    description: `Physical order document for ${orderId}`,
    content: [
      {
        attachment: {
          contentType: file.type,
          url: binaryReferenceUrl,
          title: file.name,
          size: file.size,
        },
      },
    ],
  };
}

export function createAuthorizationFormDocument(
  file: File,
  binaryReferenceUrl: string,
  patientId: string,
  orderId: string
): DocumentReference {
  return {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [
        {
          system: SYSTEMS.LOINC,
          code: EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT,
          display: 'Authorization for release of information',
        },
      ],
    },
    subject: { reference: `Patient/${patientId}` },
    date: new Date().toISOString(),
    description: `Authorization form for answer delivery - ${orderId}`,
    content: [
      {
        attachment: {
          contentType: file.type,
          url: binaryReferenceUrl,
          title: file.name,
          size: file.size,
        },
      },
    ],
  };
}

/**
 * Checks if supportingInfo already contains a document of a specific LOINC code type.
 *
 * @param medplum - Medplum client
 * @param supportingInfo - Array of supporting info references to check
 * @param loincCode - LOINC code to look for in DocumentReference.type.coding
 * @returns True if a document with that LOINC code exists, false otherwise
 */
async function hasExistingDocumentType(
  medplum: MedplumClient,
  supportingInfo: { reference?: string }[],
  loincCode: string
): Promise<boolean> {
  for (const ref of supportingInfo) {
    const docRef = ref.reference?.replace('DocumentReference/', '');
    if (!docRef) {
      continue;
    }

    try {
      const doc = await medplum.readResource('DocumentReference', docRef);
      const code = doc.type?.coding?.[0]?.code;
      if (code === loincCode) {
        return true;
      }
    } catch {
      // Document might have been deleted, skip
    }
  }
  return false;
}

/**
 * Uploads order file and/or authorization form as Binary, creates DocumentReferences,
 * and returns references to add to ServiceRequest.supportingInfo.
 * Use for both create (before creating ServiceRequest) and edit (when adding new files).
 *
 * @param medplum - Medplum client
 * @param formData - Order form data (may contain orderFile and/or authorizationFormFile)
 * @param orderId - Order identifier
 * @param existingSupportingInfo - Existing supportingInfo refs (to avoid re-uploading existing docs)
 * @returns Array of { reference: 'DocumentReference/id' } to set on supportingInfo
 */
export async function uploadOrderDocumentsAndGetRefs(
  medplum: MedplumClient,
  formData: OrderFormData,
  orderId: string,
  existingSupportingInfo: { reference?: string }[] = []
): Promise<{ reference: string }[]> {
  const refs: { reference: string }[] = [];
  const patientId = formData.patient?.id;
  if (!patientId) {
    return refs;
  }

  // Only upload order file if there isn't already an order document in supportingInfo
  if (formData.orderFile) {
    const hasOrderDoc = await hasExistingDocumentType(
      medplum,
      existingSupportingInfo,
      EXTERNAL_CS.LOINC.CODES.ORDER_DOCUMENT
    );

    if (!hasOrderDoc) {
      const orderBinary = await medplum.createBinary({
        data: formData.orderFile,
        filename: formData.orderFile.name,
        contentType: formData.orderFile.type,
      });
      const orderDoc = createOrderDocument(
        formData.orderFile,
        getReferenceString(orderBinary),
        patientId,
        orderId
      );
      const created = await medplum.createResource(orderDoc);
      if (created?.id) {
        refs.push({ reference: `DocumentReference/${created.id}` });
      }
    }
  }

  // Only upload auth form if there isn't already an auth document in supportingInfo
  if (
    hasAuthorizedPerson(formData.answerDelivery) &&
    formData.authorizationFormFile
  ) {
    const hasAuthDoc = await hasExistingDocumentType(
      medplum,
      existingSupportingInfo,
      EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT
    );

    if (!hasAuthDoc) {
      const authBinary = await medplum.createBinary({
        data: formData.authorizationFormFile,
        filename: formData.authorizationFormFile.name,
        contentType: formData.authorizationFormFile.type,
      });
      const authDoc = createAuthorizationFormDocument(
        formData.authorizationFormFile,
        getReferenceString(authBinary),
        patientId,
        orderId
      );
      const created = await medplum.createResource(authDoc);
      if (created?.id) {
        refs.push({ reference: `DocumentReference/${created.id}` });
      }
    }
  }

  return refs;
}

// -----------------------------------------------------------------------------
export const createDiagnosticReportFromReview = (
  reviewNotes: string,
  reviewResult: 'positive' | 'negative',
  serviceRequest: ServiceRequest,
  patient: Patient,
  methodologies?: string,
  references?: string,
  specimens: any[] = [],
  observations: Observation[] = [],
  status: 'preliminary' | 'final' | 'registered' = 'preliminary'
): DiagnosticReport => {
  // Map review result to SNOMED codes
  const conclusionCodeMap = {
    positive: TEST_RESULT_CODES.POSITIVE,
    negative: TEST_RESULT_CODES.NEGATIVE,
  };

  const conclusionCode = conclusionCodeMap[reviewResult];
  const extensions: any[] = [];

  if (methodologies?.trim()) {
    extensions.push({
      url: ARKON.SD.DIAGNOSTIC_REPORT.METHODOLOGIES,
      valueString: methodologies.trim(),
    });
  }

  if (references?.trim()) {
    extensions.push({
      url: ARKON.SD.DIAGNOSTIC_REPORT.REFERENCES,
      valueString: references.trim(),
    });
  }

  // TODO: use correct code and move to consts.
  // edit generated html?
  return {
    resourceType: 'DiagnosticReport',
    status: status,
    code: {
      coding: [
        {
          system: SYSTEMS.LOINC,
          code: '81247-9',
          display: 'Master HL7 genetic variant reporting panel',
        },
      ],
    },
    subject: { reference: `Patient/${patient.id}` },
    basedOn: [{ reference: `ServiceRequest/${serviceRequest.id}` }],
    issued: new Date().toISOString(),
    conclusionCode: [
      {
        coding: [
          {
            system: SYSTEMS.SNOMED,
            code: conclusionCode.code,
            display: conclusionCode.display,
          },
        ],
        text: reviewResult === 'positive' ? 'Positive' : 'Negative',
      },
    ],
    conclusion: reviewNotes || `Genetic test result: ${conclusionCode.display}`,
    ...(specimens.length > 0 && {
      specimen: specimens.map((specimen) => ({
        reference: `Specimen/${specimen.id}`,
        display: specimen.identifier?.[0]?.value || specimen.id,
      })),
    }),
    ...(observations.length > 0 && {
      result: observations.map((obs) => ({
        reference: `Observation/${obs.id}`,
      })),
    }),
    ...(extensions.length > 0 && {
      extension: extensions,
    }),
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml">
        <h3>Genetic Test Report</h3>
      </div>`,
    },
  };
};

//

// -----------------------------------------------------------------------------
export const createDiagnosticReportBundle = (
  diagnosticReport: DiagnosticReport,
  serviceRequest: ServiceRequest,
  observations: Observation[] = []
): Bundle => {
  const entries: any[] = [
    {
      fullUrl: `urn:uuid:diagnostic-report-${Date.now()}`,
      resource: diagnosticReport,
      request: { method: 'POST', url: 'DiagnosticReport' },
    },
  ];

  // Add observations if any
  observations.forEach((obs, index) => {
    entries.push({
      fullUrl: `urn:uuid:observation-${Date.now()}-${index}`,
      resource: obs,
      request: { method: 'POST', url: 'Observation' },
    });
  });

  // Update ServiceRequest to completed status and link to DiagnosticReport
  const updatedServiceRequest = {
    ...serviceRequest,
    // status: 'completed' as const,
  };

  entries.push({
    resource: updatedServiceRequest,
    request: { method: 'PUT', url: `ServiceRequest/${serviceRequest.id}` },
  });

  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  };
};

// -----------------------------------------------------------------------------
export const searchDiagnosticReportsForOrder = async (
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<DiagnosticReport[]> => {
  try {
    const reports = await medplum.searchResources('DiagnosticReport', {
      'based-on': `ServiceRequest/${serviceRequestId}`,
      _sort: '-issued',
    });
    return reports || [];
  } catch (error) {
    console.error('Error searching for diagnostic reports:', error);
    return [];
  }
};

// -----------------------------------------------------------------------------
export const createDiagnosticReportReviewTask = (
  diagnosticReport: DiagnosticReport,
  serviceRequest: ServiceRequest,
  patient: Patient,
  assignedTo?: any
): Task => {
  const orderId = serviceRequest.id;
  const reportId = diagnosticReport.id;

  // Create the link to the diagnostic report page
  const reportPageUrl = `/lims/order/${orderId}/diagnostic-report/${reportId}`;

  return {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    priority: 'routine',
    code: {
      coding: [
        {
          system: SYSTEMS.SNOMED,
          code: EXTERNAL_CS.SNOMED.CODES.DIAGNOSTIC_REPORT_REVIEW,
          display: 'Diagnostic report review',
        },
      ],
      text: 'Review Diagnostic Report',
    },
    description: `Review required for genetic test diagnostic report. Please review the report and approve if appropriate.`,
    focus: {
      reference: `DiagnosticReport/${diagnosticReport.id}`,
      display: 'Genetic Test Diagnostic Report',
    },
    for: {
      reference: `Patient/${patient.id}`,
      display: formatHumanName(patient.name?.[0]) || `Patient ${patient.id}`,
    },
    authoredOn: new Date().toISOString(),
    note: [
      {
        text: `Diagnostic report review required. View full report at: ${reportPageUrl}`,
        time: new Date().toISOString(),
      },
    ],
    input: [
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.REFERENCE,
              display: 'Reference',
            },
          ],
          text: 'Diagnostic Report Reference',
        },
        valueReference: {
          reference: `DiagnosticReport/${diagnosticReport.id}`,
          display: 'Genetic Test Diagnostic Report',
        },
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.TARGET_URL,
              display: 'Target URL',
            },
          ],
          text: 'Report Page URL',
        },
        valueString: reportPageUrl,
      },
    ],
    ...(assignedTo && {
      owner: {
        reference: `Practitioner/${assignedTo.id}`,
        display:
          formatHumanName(assignedTo.name?.[0]) ||
          `Practitioner ${assignedTo.id}`,
      },
    }),
  };
};

// -----------------------------------------------------------------------------
export const createLabResultReviewTask = (
  serviceRequest: ServiceRequest,
  patient: Patient,
  reviewNotes: string,
  reviewResult: 'positive' | 'negative',
  assignedTo?: any
): Task => {
  const orderId = serviceRequest.id;

  // Create the link to the order details page
  const orderPageUrl = `/lims/order/${orderId}`;

  return {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    priority: 'routine',
    code: {
      coding: [
        {
          system: SYSTEMS.SNOMED,
          code: EXTERNAL_CS.SNOMED.CODES.LABORATORY_RESULT_REVIEW,
          display: 'Laboratory result review',
        },
      ],
      text: 'Review Lab Results',
    },
    description: `Lab result review required for genetic test. Result: ${reviewResult}. Please review and approve for diagnostic report creation.`,
    focus: {
      reference: `ServiceRequest/${serviceRequest.id}`,
      display: 'Genetic Test Order',
    },
    for: {
      reference: `Patient/${patient.id}`,
      display: formatHumanName(patient.name?.[0]) || `Patient ${patient.id}`,
    },
    authoredOn: new Date().toISOString(),
    note: [
      {
        text: `Lab result review required. Initial notes: ${reviewNotes || 'No additional notes'}. View order at: ${orderPageUrl}`,
        time: new Date().toISOString(),
      },
    ],
    input: [
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.REFERENCE,
              display: 'Reference',
            },
          ],
          text: 'Service Request Reference',
        },
        valueReference: {
          reference: `ServiceRequest/${serviceRequest.id}`,
          display: 'Genetic Test Order',
        },
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.STRING,
              display: 'String',
            },
          ],
          text: 'Lab Result',
        },
        valueString: reviewResult,
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.STRING,
              display: 'String',
            },
          ],
          text: 'Review Notes',
        },
        valueString: reviewNotes,
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.INPUT_TYPE.CODES.TARGET_URL,
              display: 'Target URL',
            },
          ],
          text: 'Order Page URL',
        },
        valueString: orderPageUrl,
      },
    ],
    ...(assignedTo && {
      owner: {
        reference: `Practitioner/${assignedTo.id}`,
        display:
          formatHumanName(assignedTo.name?.[0]) ||
          `Practitioner ${assignedTo.id}`,
      },
    }),
  };
};

// -----------------------------------------------------------------------------
export const searchPendingReviewTasks = async (
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<Task[]> => {
  try {
    // todo: use correct code and move to consts. same code as in createLabResultReviewTask
    const tasks = await medplum.searchResources('Task', {
      focus: `ServiceRequest/${serviceRequestId}`,
      status: 'requested,in-progress',
      code: EXTERNAL_CS.SNOMED.CODES.LABORATORY_RESULT_REVIEW,
      _sort: '-authored-on',
    });

    return tasks || [];
  } catch (error) {
    console.error('Error searching for review tasks:', error);
    return [];
  }
};

// -----------------------------------------------------------------------------
export const searchPendingDiagnosticReportReviewTasks = async (
  medplum: MedplumClient,
  diagnosticReportId: string
): Promise<Task[]> => {
  try {
    const allTasks = await searchAllDiagnosticReportReviewTasks(
      medplum,
      diagnosticReportId
    );
    return allTasks.filter(
      (task: Task) =>
        task.status === 'requested' || task.status === 'in-progress'
    );
  } catch (error) {
    console.error(
      'Error searching for pending diagnostic report review tasks:',
      error
    );
    return [];
  }
};

// -----------------------------------------------------------------------------
export const searchAllDiagnosticReportReviewTasks = async (
  medplum: MedplumClient,
  diagnosticReportId: string
): Promise<Task[]> => {
  try {
    // Search for all tasks that focus on this diagnostic report
    const tasks = await medplum.searchResources('Task', {
      focus: `DiagnosticReport/${diagnosticReportId}`,
      _sort: '-authored-on',
    });

    const reviewTasks =
      tasks?.filter((task: Task) => {
        const hasReviewCode = task.code?.coding?.some(
          (coding) =>
            coding.code === EXTERNAL_CS.SNOMED.CODES.DIAGNOSTIC_REPORT_REVIEW
        );

        const hasReviewText = task.code?.text?.toLowerCase().includes('review');
        return hasReviewCode || hasReviewText;
      }) || [];

    return reviewTasks;
  } catch (error) {
    console.error(
      'Error searching for all diagnostic report review tasks:',
      error
    );
    return [];
  }
};

// -----------------------------------------------------------------------------
export const checkAllReviewersApproved = async (
  medplum: MedplumClient,
  diagnosticReportId: string
): Promise<{
  allApproved: boolean;
  totalTasks: number;
  approvedTasks: number;
  pendingTasks: number;
  approvedReviewerIds: string[];
  pendingReviewerIds: string[];
}> => {
  try {
    // Get all review tasks for this diagnostic report
    const allTasks = await searchAllDiagnosticReportReviewTasks(
      medplum,
      diagnosticReportId
    );

    let approvedTasks = 0;
    let pendingTasks = 0;
    const approvedReviewerIds: string[] = [];
    const pendingReviewerIds: string[] = [];
    for (const task of allTasks) {
      const reviewerId = task.owner?.reference?.replace('Practitioner/', '');

      // Check task outputs for the latest review decision
      const reviewDecisionOutput = task.output?.find(
        (output) => output.type?.coding?.[0]?.code === 'review-decision'
      );

      const latestReviewDecision = reviewDecisionOutput?.valueString;

      if (latestReviewDecision === 'approved') {
        // Task has been approved (status should be 'completed')
        approvedTasks++;
        if (reviewerId) approvedReviewerIds.push(reviewerId);
      } else {
        // Task is either pending or has changes requested
        pendingTasks++;
        if (reviewerId) pendingReviewerIds.push(reviewerId);
      }
    }

    const totalTasks = allTasks.length;
    const allApproved =
      totalTasks > 0 && pendingTasks === 0 && approvedTasks === totalTasks;

    return {
      allApproved,
      totalTasks,
      approvedTasks,
      pendingTasks,
      approvedReviewerIds,
      pendingReviewerIds,
    };
  } catch (error) {
    console.error('Error checking reviewer approvals:', error);
    return {
      allApproved: false,
      totalTasks: 0,
      approvedTasks: 0,
      pendingTasks: 0,
      approvedReviewerIds: [],
      pendingReviewerIds: [],
    };
  }
};

// -----------------------------------------------------------------------------
export const findReviewerTask = async (
  medplum: MedplumClient,
  diagnosticReportId: string,
  reviewerId: string
): Promise<Task | null> => {
  try {
    // First try to find by owner reference
    let tasks = await medplum.searchResources('Task', {
      focus: `DiagnosticReport/${diagnosticReportId}`,
      code: EXTERNAL_CS.SNOMED.CODES.DIAGNOSTIC_REPORT_REVIEW,
      owner: `Practitioner/${reviewerId}`,
      _count: 1,
    });

    if (tasks?.[0]) {
      return tasks[0];
    }

    // If not found by owner, search all tasks and check if this reviewer has any task
    tasks = await medplum.searchResources('Task', {
      focus: `DiagnosticReport/${diagnosticReportId}`,
      code: EXTERNAL_CS.SNOMED.CODES.DIAGNOSTIC_REPORT_REVIEW,
    });

    // Find task where the owner matches the current reviewer
    const reviewerTask = tasks?.find((task: Task) => {
      const ownerRef = task.owner?.reference;
      return (
        ownerRef === `Practitioner/${reviewerId}` ||
        ownerRef?.endsWith(`/${reviewerId}`)
      );
    });

    return reviewerTask || null;
  } catch (error) {
    console.error('Error finding reviewer task:', error);
    return null;
  }
};

// -----------------------------------------------------------------------------
export const completeReviewerTask = async (
  medplum: MedplumClient,
  task: Task,
  reviewStatus: 'approved' | 'changes-requested',
  comments: string
): Promise<Task> => {
  // Only mark task as completed if approved
  // If changes are requested, keep task as 'in-progress' so reviewer can review again
  const taskStatus = reviewStatus === 'approved' ? 'completed' : 'in-progress';

  // Update task status and add review outcome to the task
  const updatedTask: Task = {
    ...task,
    status: taskStatus,
    note: [
      ...(task.note || []),
      {
        text: `Review ${reviewStatus === 'approved' ? 'completed' : 'updated'}: ${reviewStatus}. Comments: ${comments}`,
        time: new Date().toISOString(),
      },
    ],
    // Add review outcome as an output
    output: [
      ...(task.output || []),
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.CODES.REVIEW_DECISION,
              display: 'Review Decision',
            },
          ],
          text: 'Review Decision',
        },
        valueString: reviewStatus,
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.CODES.REVIEW_COMMENTS,
              display: 'Review Comments',
            },
          ],
          text: 'Review Comments',
        },
        valueString: comments,
      },
      {
        type: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.TASK.OUTPUT_TYPE.CODES.REVIEW_TIMESTAMP,
              display: 'Review Timestamp',
            },
          ],
          text: 'Review Timestamp',
        },
        valueString: new Date().toISOString(),
      },
    ],
  };

  return medplum.updateResource(updatedTask);
};

// -----------------------------------------------------------------------------
export const addReviewStatusExtension = (
  diagnosticReport: DiagnosticReport,
  reviewStatus: {
    allApproved: boolean;
    totalTasks: number;
    approvedTasks: number;
    pendingTasks: number;
    approvedReviewerIds: string[];
    pendingReviewerIds: string[];
  }
): DiagnosticReport => {
  // Build the extension array dynamically to avoid empty values
  const extensionEntries: any[] = [
    {
      url: 'allApproved',
      valueBoolean: reviewStatus.allApproved,
    },
    {
      url: 'totalReviewers',
      valueInteger: reviewStatus.totalTasks,
    },
    {
      url: 'approvedCount',
      valueInteger: reviewStatus.approvedTasks,
    },
    {
      url: 'pendingCount',
      valueInteger: reviewStatus.pendingTasks,
    },
    {
      url: 'lastUpdated',
      valueDateTime: new Date().toISOString(),
    },
  ];

  // Add approved reviewers as separate extensions
  reviewStatus.approvedReviewerIds.forEach((reviewerId) => {
    extensionEntries.push({
      url: 'approvedReviewer',
      valueString: reviewerId,
    });
  });

  // Add pending reviewers as separate extensions
  reviewStatus.pendingReviewerIds.forEach((reviewerId) => {
    extensionEntries.push({
      url: 'pendingReviewer',
      valueString: reviewerId,
    });
  });

  const reviewExtension = {
    url: ARKON.SD.DIAGNOSTIC_REPORT.REVIEW_STATUS,
    extension: extensionEntries,
  };

  // Remove existing review status extension if present
  const extensionsWithoutReview = (diagnosticReport.extension || []).filter(
    (ext) => ext.url !== ARKON.SD.DIAGNOSTIC_REPORT.REVIEW_STATUS
  );

  return {
    ...diagnosticReport,
    extension: [...extensionsWithoutReview, reviewExtension],
  };
};

// -----------------------------------------------------------------------------
export const getReviewStatusFromExtension = (
  diagnosticReport: DiagnosticReport
): {
  allApproved: boolean;
  totalTasks: number;
  approvedTasks: number;
  pendingTasks: number;
  approvedReviewerIds: string[];
  pendingReviewerIds: string[];
  lastUpdated?: string;
} | null => {
  const reviewExtension = diagnosticReport.extension?.find(
    (ext) => ext.url === ARKON.SD.DIAGNOSTIC_REPORT.REVIEW_STATUS
  );

  if (!reviewExtension?.extension) {
    return null;
  }

  const getExtensionValue = (url: string) => {
    const ext = reviewExtension.extension?.find((e) => e.url === url);
    return (
      ext?.valueBoolean ??
      ext?.valueInteger ??
      ext?.valueString ??
      ext?.valueDateTime
    );
  };

  // Get all approved and pending reviewer IDs from separate extensions
  const approvedReviewerIds =
    (reviewExtension.extension
      ?.filter((ext) => ext.url === 'approvedReviewer')
      .map((ext) => ext.valueString)
      .filter(Boolean) as string[]) || [];

  const pendingReviewerIds =
    (reviewExtension.extension
      ?.filter((ext) => ext.url === 'pendingReviewer')
      .map((ext) => ext.valueString)
      .filter(Boolean) as string[]) || [];
  return {
    allApproved: (getExtensionValue('allApproved') as boolean) || false,
    totalTasks: (getExtensionValue('totalReviewers') as number) || 0,
    approvedTasks: (getExtensionValue('approvedCount') as number) || 0,
    pendingTasks: (getExtensionValue('pendingCount') as number) || 0,
    approvedReviewerIds,
    pendingReviewerIds,
    lastUpdated: getExtensionValue('lastUpdated') as string,
  };
};

export const getOrderIdSuggestions = async (
  medplum: MedplumClient,
  partialId: string
): Promise<string[]> => {
  const q = partialId.trim();
  if (q.length < 2) {
    return [];
  }
  try {
    const results: ServiceRequest[] =
      (await medplum.searchResources('ServiceRequest', {
        category: `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
        _count: 10, // Get recent orders for client-side filtering
        _sort: '-authored',
      })) || [];

    // Filter results to those containing the query (client-side)
    const ids = Array.from(
      new Set(
        results
          .flatMap((sr) => sr.identifier ?? [])
          .map((id) => id?.value)
          .filter(
            (v): v is string =>
              typeof v === 'string' &&
              v.length > 0 &&
              v.toLowerCase().includes(q.toLowerCase())
          )
      )
    );

    return ids.slice(0, 10);
  } catch (error) {
    console.error('[getOrderIdSuggestions] Error:', error);
    return [];
  }
};

// -----------------------------------------------------------------------------
export const getPatientsByName = async (
  medplum: MedplumClient,
  nameQuery: string
): Promise<Patient[]> => {
  const q = nameQuery.trim();
  if (q.length < 2) {
    return [];
  }
  try {
    const results: Patient[] =
      (await medplum.searchResources('Patient', {
        name: q,
        _count: 10,
        _sort: 'name',
      })) || [];
    return results;
  } catch (error) {
    console.error('[getPatientsByName] Error:', error);
    return [];
  }
};

// -----------------------------------------------------------------------------
export const getPatientNameSuggestions = async (
  medplum: MedplumClient,
  nameQuery: string
): Promise<string[]> => {
  const patients = await getPatientsByName(medplum, nameQuery);
  const names = patients
    .map((p) => formatHumanName(p.name?.[0]))
    .filter((n): n is string => typeof n === 'string' && n.trim().length > 0);
  return Array.from(new Set(names)).slice(0, 10);
};

// -----------------------------------------------------------------------------
export const getSpecimenIdSuggestions = async (
  medplum: MedplumClient,
  partialId: string
): Promise<string[]> => {
  const q = partialId.trim();
  if (q.length < 2) {
    return [];
  }
  try {
    const results: Specimen[] =
      (await medplum.searchResources('Specimen', {
        _count: 10,
        _sort: '-_lastUpdated',
      })) || [];

    const ids = Array.from(
      new Set(
        results
          .flatMap((s) => s.identifier ?? [])
          .map((id) => id?.value)
          .filter(
            (v): v is string =>
              typeof v === 'string' &&
              v.length > 0 &&
              v.toLowerCase().includes(q.toLowerCase())
          )
      )
    );

    return ids.slice(0, 10);
  } catch (error) {
    console.error('[getSpecimenIdSuggestions] Error:', error);
    return [];
  }
};
